import React, {useMemo} from "react";

import MaterialTable, {Column, MTableBodyRow} from "material-table";
import {localization, modifyDefaultOptions} from "../../helpers/table";
import {monthsArray} from "../../stores/StratProject/StrategyStore";
import {tableIcons} from "../../helpers";
import {IndicatorsFilters, milestoneIndicatorsLegend, MilestoneIndicatorTypes} from "./ResultsIndicator";
import {Tooltip} from "@material-ui/core";

export const ResultsTable: React.FC<{ results: Milestone[], filters: IndicatorsFilters }> = React.memo(({results, filters}) => {
    const firstCellWidth = results.some(result => !!result.parentId) ? 512 : 560

    return (
        <div className='resultIndicatorsInner'>
            <MaterialTable
                data={results}
                columns={[
                    {
                        title: 'Плановый результат / вехи',
                        field: 'name',
                        cellStyle: {
                            minWidth: firstCellWidth,
                            borderBottom: '1px solid #BFBFD0'
                        },
                        render: (rowData) => {
                            return <div>{rowData.tableData.path[0] + 1}.{rowData.parentId ? rowData.tableData.path[1] + 1 : ''} {rowData.name}</div>
                        }
                    },
                    {
                        title: 'Срок (план)',
                        field: 'planDate',
                        cellStyle: {
                            minWidth: 150,
                            borderBottom: '1px solid #BFBFD0',
                            borderLeft: '1px solid #BFBFD0',
                        },
                        render: (rowData) => `${monthsArray[rowData.monthPlan]} ${rowData.yearPlan}`
                    },
                    ...monthsArray.map((month, index) => ({
                        title: month.slice(0, 3) + '.',
                        field: 'month' + index,
                        cellStyle: {
                            minWidth: 85,
                            borderBottom: '1px solid #BFBFD0',
                            borderLeft: '1px solid #BFBFD0',
                            borderRight: index === monthsArray.length - 1 ? '1px solid #BFBFD0' : 'none',
                        },
                        render: (rowData) => {
                            const milestoneAchievingInfo = getCurrentResults(
                                !!rowData.parentId,
                                rowData.status,
                                rowData.monthPlan,
                                rowData.yearPlan,
                                rowData.monthFact,
                                rowData.yearFact
                            );
                            const milestoneLegendInfo = milestoneIndicatorsLegend.find(legendItem => legendItem.areaName === milestoneAchievingInfo.type)
                            const isFutureResult = !rowData.parentId && !!filters.year && rowData.yearPlan > filters.year ;
                            return <div className='d-flex align-items-center justify-content-center'>
                                {!isFutureResult && milestoneLegendInfo && index === milestoneAchievingInfo.month && <Tooltip
                                  title={
                                      milestoneAchievingInfo.type.slice(0, 4) === 'plan' ?
                                          <p>
                                              <span>УГТ - {rowData.trl?.id || ''} "{rowData.trl?.descr || ''}",</span>
                                              <br/>
                                              {rowData.personsInChargeSmall.map(({
                                                userName: {
                                                    firstName,
                                                    lastName,
                                                    middleName
                                                }
                                              }) => <span>{lastName} {firstName} {middleName || ''}</span>)}
                                          </p> :
                                          <p>
                                              <span>{rowData.result}</span>,
                                              <br/>
                                              <span>УГТ - {rowData.trlFact?.id || ''} "{rowData.trlFact?.descr || ''}"</span>,
                                              <br/>
                                              {rowData.personsInChargeSmall.map(({
                                                userName: {
                                                    firstName,
                                                    lastName,
                                                    middleName
                                                }
                                              }) => <span>{lastName} {firstName} {middleName || ''}</span>)}
                                          </p>
                                  }
                                  style={{
                                      fontSize: 14
                                  }}
                                >
                                  <img src={milestoneLegendInfo.icon} style={{width: 27, height: 27}}/>
                                </Tooltip>}
                            </div>
                        }
                    } as Column<Milestone>))
                ]}
                components={{
                    Row: props => {
                        const tableData = {...props.data.tableData, path: props.path}
                        props.data.tableData = tableData
                        return <MTableBodyRow {...props}/>
                    }
                }}
                options={modifyDefaultOptions({
                    filtering: false,
                    sorting: false,
                    header: false,
                    toolbar: false,
                    rowStyle: (rowData) => ({
                        backgroundColor: !!rowData.parentId ? 'white' : '#F4F3FF'
                    })
                })}
                localization={localization}
                icons={tableIcons}
                parentChildData={(row, rows) => rows.find(a => a.id === row.parentId)}
            />
        </div>
    )
})

export type Milestone = {
    name: string,
    strategyId: number,
    parentId?: number,
    id: number,
    monthPlan: number,
    yearPlan: number,
    monthFact: number,
    yearFact: number,
    status: string,
    trl?: {
        descr: string,
        id: number
    },
    trlFact?: {
        descr: string,
        id: number
    },
    personsInChargeSmall: PersonInChargeSmall[],
    result?: string
}

export type PersonInChargeSmall = {
    userId: string,
    userName: {
        firstName: string,
        lastName: string,
        middleName?: string
    }
}

const getCurrentResults = (isMilestone: boolean, status: string, monthPlan: number, yearPlan: number, monthFact?: number, yearFact?: number): {
    month: number,
    type: MilestoneIndicatorTypes
} => {
    const currentDate = new Date()
    const currentMonth = currentDate.getMonth()
    const currentYear = currentDate.getFullYear()
    if ((!monthFact && monthFact !== 0) || (!yearFact && yearFact !== 0) || ['onProjOffice', 'inProgress', 'returned'].includes(status)) {
        return monthPlan >= currentMonth && yearPlan === currentYear || yearPlan > currentYear ?
            {
                month: monthPlan,
                type: isMilestone ? MilestoneIndicatorTypes.planMilestone : MilestoneIndicatorTypes.planResult
            } :
            {
                month: monthPlan,
                type: isMilestone ? MilestoneIndicatorTypes.planMilestoneNotAchieved : MilestoneIndicatorTypes.planResultNotAchieved
            }
    }
    return yearFact > yearPlan || yearFact === yearPlan && monthFact > monthPlan ?
        {
            month: monthFact,
            type: isMilestone ? MilestoneIndicatorTypes.factMilestoneNotAchieved : MilestoneIndicatorTypes.factResultNotAchieved
        } :
        {
            month: monthFact,
            type: isMilestone ? MilestoneIndicatorTypes.factMilestone : MilestoneIndicatorTypes.factResult
        }
}

export default ResultsTable
